import type { BillingAccount, MemberAgeing } from './api';

/**
 * Hero balance state.
 *
 * The eyebrow LABEL carries the direction of the money, so the amount is
 * always rendered unsigned: "YOU OWE $120" reads unambiguously where a bare
 * "-$120" does not (on a statement a minus means credit; in banking it means
 * overdrawn). "Owing" and "overdue" share the same label on purpose — the
 * severity is carried by colour and the status pill, not by re-reading it.
 */
export type BalanceStatus = 'settled' | 'owing' | 'overdue' | 'credit';

export interface BalanceState {
  status: BalanceStatus;
  /** Sentence case; the `.eyebrow` utility uppercases it visually, which keeps
   *  screen readers reading words rather than spelling out capitals. */
  label: string;
  /** Never negative — the label says which way the money flows. */
  amount: number;
  /** Age of the oldest arrears bucket, or null when nothing is past due. */
  daysLate: number | null;
}

/** Balances within half a cent of zero are settled, not owing or in credit. */
const EPSILON = 0.005;

/** Oldest bucket first, so the first hit is the worst one. */
const ARREARS_BUCKETS = [
  ['days_120_plus', 120],
  ['days_90', 90],
  ['days_60', 60],
  ['days_30', 30],
] as const;

function oldestArrearsDays(ageing: MemberAgeing | null | undefined): number | null {
  if (!ageing) return null;
  const bucket = ARREARS_BUCKETS.find(([key]) => ageing[key] > EPSILON);
  return bucket ? bucket[1] : null;
}

/**
 * Everything the hero card needs, from the two payloads the API already
 * returns. `ageing` is optional — it is licensed separately, and without it an
 * outstanding balance can only be reported as owing, never as overdue.
 */
export function deriveBalanceState(
  account: BillingAccount,
  ageing?: MemberAgeing | null,
): BalanceState {
  // 1. A negative balance is money the estate is holding for the member.
  if (account.balance < -EPSILON) {
    return { status: 'credit', label: 'In credit', amount: -account.balance, daysLate: null };
  }

  // 2. Nothing meaningfully outstanding.
  if (account.balance <= EPSILON) {
    return { status: 'settled', label: 'Nothing due', amount: 0, daysLate: null };
  }

  // 3. Owing — the ageing summary, when available, says how late it has become.
  const daysLate = oldestArrearsDays(ageing);
  return {
    status: daysLate === null ? 'owing' : 'overdue',
    label: 'You owe',
    amount: account.balance,
    daysLate,
  };
}
